import { LocaleDict } from "@/i18n";
import api from "@/lib/axiosConfig";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import {
  CompleteSellable,
  PaginatedReturn,
  PagingParameters,
  SellableItemType,
  sortingToUrlParam,
} from "./dtos";

export interface FullShopArticle {
  id: string;

  itemType: SellableItemType;

  currentQuantity: number;

  additionalInfo?: LocaleDict<string | null>;

  sellableContent: CompleteSellable;

  priceAmount: number;

  updatedAt: Date;
  createdAt?: Date;
}

export type BaseArticle = Pick<
  FullShopArticle,
  "currentQuantity" | "priceAmount" | "additionalInfo" | "itemType"
>;

export type CreateArticle = BaseArticle & { sellableId: string };

export type UpdateArticle = Omit<Partial<BaseArticle>, "itemType">;

export type SellingStatus = "SOLD" | "UNSOLD";

export const purchaseStatusValues = [
  "WAITING_PAYMENT",
  "PAID_FOR",
  "SHIPPED",
  "IN_DELIVERY",
  "DELIVERED",
] as const;

export type PurchaseStatus = (typeof purchaseStatusValues)[number];

export const getSellingStatus = (article: FullShopArticle): SellingStatus => {
  return article.currentQuantity <= 0 ? "SOLD" : "UNSOLD";
};

export const getArticles = async (
  paging: PagingParameters = { page: 0, pageSize: 50 },
  sorting: SortingState = [],
  sellingStatus?: SellingStatus,
) => {
  const { data } = await api.get<PaginatedReturn<FullShopArticle>>(
    "back/api/articles",
    {
      params: {
        ...paging,
        sort: sortingToUrlParam(sorting),
        ...(sellingStatus ? { sellingStatus } : {}),
      },
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );

  return data;
};

export const useGetArticles = (
  paging?: PagingParameters,
  sorting?: SortingState,
  sellingStatus?: SellingStatus,
) => {
  return useQuery({
    queryKey: ["articles", paging, sorting, sellingStatus],
    queryFn: () => getArticles(paging, sorting, sellingStatus),
    placeholderData: keepPreviousData,
  });
};

const createArticle = async (create: CreateArticle) => {
  return await api.post<FullShopArticle>("back/api/articles", create, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
};

export const useCreateArticle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createArticle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sellables"] });
      queryClient.invalidateQueries({ queryKey: ["articles"] });
      queryClient.invalidateQueries({ queryKey: ["artworks"] });
    },
  });
};

const deleteArticle = async (articleId: string) => {
  return await api.delete<FullShopArticle>("back/api/articles/" + articleId, {
    headers: {
      instance: localStorage.getItem("instance"),
    },
  });
};

export const useDeleteArticle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteArticle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles"] });
    },
  });
};

const updateArticle = async ({
  articleId,
  updateArticle,
}: {
  articleId: string;
  updateArticle: UpdateArticle;
}) => {
  return await api.patch<FullShopArticle>(
    "back/api/articles/" + articleId,
    updateArticle,
    {
      headers: {
        instance: localStorage.getItem("instance"),
      },
    },
  );
};

export const useUpdateArticle = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateArticle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["articles"] });
    },
  });
};
